import {isEmpty} from 'lodash';
import React, {Fragment, ReactElement, useEffect, useState} from 'react';
import Table from 'src/components/SimpleTable/SimpleTable';
import EducatorVerificationService, {
  PendingEducator,
} from 'src/services/EducatorVerificationService';
import Button from 'src/components/button';
import VerifyButton from 'src/views/admin/EducatorVerification/VerifyButton';
import RejectForm from 'src/views/admin/EducatorVerification/RejectForm';
import {Link} from 'react-router-dom';
import {useBoolean} from 'src/hooks/useBoolean';
import Loading from 'shared/components/loading';
import {Box} from '@mui/material';
import styles from './styles.module.scss';

const columns = [
  'Official ID',
  'Name',
  'Email',
  'Institution',
  'Linkedin Profile',
  'Onboarded',
  'Role',
  'Connections',
  'Website',
  'Actions',
];

const googleLinkFor = (educator: PendingEducator): ReactElement => {
  const googleSearchUrl =
    'https://www.google.com/search?q=' +
    educator.firstName +
    '+' +
    educator.lastName +
    '+' +
    educator.email +
    '+' +
    educator.institution.name;

  return (
    <a target="_blank" href={googleSearchUrl} rel="noreferrer">
      {educator.email}
    </a>
  );
};

const EducatorVerificationApp = (): ReactElement => {
  const [pendingEducators, setPendingEducators] = useState<PendingEducator[]>([]);
  const [selectedEducator, setSelectedEducator] = useState<PendingEducator | null>(null);
  const loading = useBoolean(false);

  const getVerificationList = async (): Promise<void> => {
    try {
      loading.setTrue();
      const pendingList = await EducatorVerificationService.index();
      setPendingEducators(pendingList);
    } catch (err) {
      console.log(err);
    } finally {
      loading.setFalse();
    }
  };

  useEffect(() => {
    getVerificationList();
  }, []);

  if (loading.value) {
    return <Loading />;
  }

  const verificationActions = (educator: PendingEducator): ReactElement => (
    <Box display="flex" flexDirection="column" px={1}>
      <VerifyButton educator={educator} />
      <Button
        size="small"
        onClick={() => {
          setSelectedEducator(educator);
        }}
      >
        Reject
      </Button>
    </Box>
  );

  const unselectEducator = () => {
    setSelectedEducator(null);
  };

  return (
    <>
      <Box my={4}>
        <h1 className="shared-font headline">Educator Verification</h1>
      </Box>
      {selectedEducator && (
        <RejectForm
          educator={selectedEducator}
          closeForm={unselectEducator}
          refreshList={getVerificationList}
        />
      )}
      <Table>
        <Table.Header centerText columns={columns.map((column) => ({value: column}))} />
        <tbody>
          {isEmpty(pendingEducators) && (
            <tr>
              <td>No educators to verify.</td>
            </tr>
          )}
          {pendingEducators.map((educator, i) => (
            <tr key={i} className={styles.tr}>
              {[
                <img key={i} height={50} src={`https:${educator.officialIdUrl}`} />,
                <Link
                  key={i}
                  to={`/admin/tools/educator-inspection?email=${encodeURIComponent(
                    educator.email
                  )}`}
                >
                  {educator.firstName} {educator.lastName}
                </Link>,
                googleLinkFor(educator),
                educator.institution.name,
                educator.linkedinProfile || 'N/A',
                isEmpty(educator.onboardingCompletedAt) ? 'No' : 'Yes',
                educator.type,
                educator.connectionCount,
                educator.websiteForEmployment || 'N/A',
                verificationActions(educator),
              ].map((data, j) => (
                <td key={j} style={{textAlign: 'center'}}>
                  {data}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default EducatorVerificationApp;
